import React, { useCallback } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";
import type { StylesConfig } from "react-select";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { format } from "date-fns";
import { capitalize } from "lodash";

import { AppContext } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { isNotNull } from "~/lib/utils";
import type { TemplateOption } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { CRM_USER_SETTINGS_UPDATED_EVENT } from "~/settings/Integrations/utils";
import type { SalesforceObjectRecordType } from "~/stream/SideBar/CRMPanel/Salesforce/ObjectCard";
import {
	type SalesforceAccountNodeType,
	type SalesforceCaseNodeType,
	type SalesforceContactNodeType,
	type SalesforceLeadNodeType,
	type SalesforceOpportunityNodeType,
	type SalesforceOptionType,
	SalesforceSelect,
} from "~/stream/SideBar/CRMPanel/Salesforce/SalesforceSelect";
import { Button, Checkbox } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import FellowLink from "~/ui/Link";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import { getDateStringForSendNoteModal, getRecapTemplateOptions, recapSelectorStyles } from "../utils";
import { NOTE, SEARCH_TYPE_PARENT, SEARCH_TYPE_WHAT, SEARCH_TYPE_WHO, TASK } from "./constants";
import { convertGraphQLTypenamesToSimpleTypes } from "./utils";

import type { SalesforceSendNotesAsNoteMutation as SalesforceSendNotesAsNoteMutationType } from "~/__generated__/SalesforceSendNotesAsNoteMutation.graphql";
import type { SalesforceSendNotesAsTaskMutation as SalesforceSendNotesAsTaskMutationType } from "~/__generated__/SalesforceSendNotesAsTaskMutation.graphql";
import type { SalesforceSendNotesAutomationQuery } from "~/__generated__/SalesforceSendNotesAutomationQuery.graphql";
import type { SalesforceSendNotesModalQuery } from "~/__generated__/SalesforceSendNotesModalQuery.graphql";
import type { SalesforceInstanceType } from "~/__generated__/SendNotesModal_viewer.graphql";

import styles from "./css/SalesforceSendNotes.module.css";

const SALESFORCE_AUTOMATION_PRO_TIP_DISMISS_KEY = "salesforce-automation-pro-tip-dismiss-key";

const DataTypeOptions: readonly SelectorOption[] = [
	{ value: TASK, label: capitalize(TASK) },
	{ value: NOTE, label: capitalize(NOTE) },
];

const salesforceSendNotesModalQuery = graphql`
	query SalesforceSendNotesModalQuery($noteId: ID!) {
		note: node(id: $noteId) {
			... on Note {
				title
				eventStart
				eventEnd
				event {
					isAllDay
				}
				meetingRecordings {
					id
					activeSummary {
						id
						templatedRecaps
						activeRecapTemplate
					}
				}
			}
		}
	}
`;

const salesforceSendNotesAutomationQuery = graphql`
	query SalesforceSendNotesAutomationQuery($noteId: ID!) {
		note: node(id: $noteId) {
			... on Note {
				sendNoteToCrmAutomationIsValid
				sendNoteAutomationIsActive: sendNoteToSalesforceAutomationIsActive
			}
		}
	}
`;

const sendTaskMutation = graphql`
	mutation SalesforceSendNotesAsTaskMutation(
		$noteId: String!
		$salesforceWho: String
		$salesforceWhat: String
		$includeRecap: Boolean!
		$recapTemplateType: RecapTemplateType
	) {
		sendTaskToSalesforce(
			input: {
				noteId: $noteId
				salesforceWho: $salesforceWho
				salesforceWhat: $salesforceWhat
				includeRecap: $includeRecap
				recapTemplateType: $recapTemplateType
			}
		) {
			id
			url
			noteVersion
			salesforce {
				lastUsedSalesforceInstanceType
			}
		}
	}
`;

const sendNoteMutation = graphql`
	mutation SalesforceSendNotesAsNoteMutation(
		$noteId: String!
		$salesforceParentIds: [String!]!
		$includeRecap: Boolean!
		$recapTemplateType: RecapTemplateType
	) {
		sendNoteToSalesforce(
			input: {
				noteId: $noteId
				salesforceParentIds: $salesforceParentIds
				includeRecap: $includeRecap
				recapTemplateType: $recapTemplateType
			}
		) {
			id
			url
			error
			noteVersion
			salesforce {
				lastUsedSalesforceInstanceType
			}
		}
	}
`;

function contactItemToNode<NodeTypename extends SalesforceOptionType["__typename"]>(
	nodeTypename: NodeTypename,
	nodeData: Omit<
		Extract<
			SalesforceOptionType,
			{
				__typename: NodeTypename;
			}
		>,
		"__typename"
	>,
) {
	return {
		...nodeData,
		__typename: nodeTypename,
	};
}

function SalesforceObjectToSearchResponseNode(
	who: SalesforceObjectRecordType,
): SalesforceContactNodeType | SalesforceLeadNodeType | null {
	if (who.name === "Contact") {
		return {
			id: who.id,
			url: who.url,
			name: who.value ?? "",
			title: who.fields.find(f => f.name === "Title")?.value ?? null,
			email: who.fields.find(f => f.name === "Email")?.value ?? null,
			__typename: "SalesforceContactNode",
		};
	} else if (who.name === "Lead") {
		return {
			id: who.id,
			url: who.url,
			name: who.value ?? "",
			company: who.fields.find(f => f.name === "Company")?.value ?? "",
			__typename: "SalesforceLeadNode",
		};
	}

	return null;
}

function whoToWhatOptions(who: SalesforceObjectRecordType): SalesforceOptionType[] {
	let accountData: SalesforceAccountNodeType | null = null;
	let opportunitiesData: SalesforceOpportunityNodeType[] = [];
	let casesData: SalesforceCaseNodeType[] = [];

	const accountRef = who.references.find(r => r.name === "Account");

	if (accountRef) {
		accountData = {
			id: accountRef.id,
			url: accountRef.url,
			name: accountRef.value ?? "",
			__typename: "SalesforceAccountNode",
		};
	}

	opportunitiesData = who.references
		.filter(r => r.name === "Opportunity")
		.map(o => ({
			id: o.id,
			url: o.url,
			name: o.value ?? "",
			amount: o.fields.find(f => f.name === "Amount")?.value ?? null,
			__typename: "SalesforceOpportunityNode",
		}));

	casesData = who.references
		.filter(r => r.name === "Case")
		.map(c => ({
			id: c.id,
			url: c.url,
			subject: c.fields.find(f => f.name === "Subject")?.value ?? null,
			caseNumber: c.fields.find(f => f.name === "CaseNumber")?.value ?? "",
			description: c.fields.find(f => f.name === "Description")?.value ?? null,
			__typename: "SalesforceCaseNode",
		}));

	const account = accountData ? contactItemToNode("SalesforceAccountNode", accountData) : false;

	const opportunities =
		opportunitiesData.length > 0 ? opportunitiesData.map(o => contactItemToNode("SalesforceOpportunityNode", o)) : [];

	const cases = casesData.length > 0 ? casesData.map(c => contactItemToNode("SalesforceCaseNode", c)) : [];

	return [...(account ? [account] : []), ...opportunities, ...cases];
}

type SalesforceSendNotesModalProps = {
	noteId: string;
	onClose: () => void;
	options: readonly SalesforceObjectRecordType[];
	defaultOption?: SalesforceObjectRecordType;
	modalMode: boolean;
	lastTypeUsed?: SalesforceInstanceType;
	enhancedNotesEnabled?: boolean;
};

export const SalesforceSendNotesModal: React.FunctionComponent<SalesforceSendNotesModalProps> = props => {
	const { noteId, options, onClose, defaultOption, modalMode, lastTypeUsed, enhancedNotesEnabled } = props;
	const data = useLazyLoadQuery<SalesforceSendNotesModalQuery>(salesforceSendNotesModalQuery, { noteId });
	const [automationDataRefetchKey, setAutomationDataRefetchKey] = React.useState(0);
	const sendNoteAutomationData = useLazyLoadQuery<SalesforceSendNotesAutomationQuery>(
		salesforceSendNotesAutomationQuery,
		{ noteId },
		{ fetchPolicy: "store-and-network", fetchKey: automationDataRefetchKey },
	);
	const [isLoading, setIsLoading] = React.useState(false);

	// These types are the same from SalesforceSelectSearchQueryResponse
	const whoOptions = options.map(o => SalesforceObjectToSearchResponseNode(o)).filter(isNotNull);
	const defaultWhoOption: SalesforceContactNodeType | SalesforceLeadNodeType | null = defaultOption
		? SalesforceObjectToSearchResponseNode(defaultOption)
		: null;

	const [selectedOptionWho, _setSelectedOptionWho] = React.useState<SalesforceOptionType | null>(defaultWhoOption);
	const [selectedOptionWhat, _setSelectedOptionWhat] = React.useState<SalesforceOptionType | null>(null);
	const [selectedOptionParents, setSelectedOptionParents] = React.useState<readonly SalesforceOptionType[] | null>(
		null,
	);
	const [includeRecap, setIncludeRecap] = React.useState(true);
	const [automationProTipDismissed, setAutomationProTipDismissed] = React.useState(
		store.has(SALESFORCE_AUTOMATION_PRO_TIP_DISMISS_KEY),
	);

	const shouldDisableIncludeRecapCheckbox = data.note?.meetingRecordings?.length === 0;

	const sendNotesAutomationIsActive = sendNoteAutomationData.note?.sendNoteAutomationIsActive;

	const recapTemplateOptions = getRecapTemplateOptions(
		data.note?.meetingRecordings?.[0]?.activeSummary?.templatedRecaps ?? {},
	);

	const shouldShowRecapTemplateOptions = data.note?.meetingRecordings?.length === 1 && recapTemplateOptions.length > 1;

	const [selectedRecapTemplate, setSelectedRecapTemplate] = React.useState<SingleValue<TemplateOption>>(
		recapTemplateOptions.find(o => o.value === data.note?.meetingRecordings?.[0]?.activeSummary?.activeRecapTemplate) ??
			recapTemplateOptions[0],
	);

	const refetchAutomationStatus = React.useCallback(() => {
		setAutomationDataRefetchKey(key => key + 1);
	}, []);

	React.useEffect(() => {
		window.addEventListener(CRM_USER_SETTINGS_UPDATED_EVENT, refetchAutomationStatus);
		return () => window.removeEventListener(CRM_USER_SETTINGS_UPDATED_EVENT, refetchAutomationStatus);
	}, [refetchAutomationStatus]);

	const { openSettingsModal } = React.useContext(AppContext);
	const openIntegrationSettings = React.useCallback(() => {
		openSettingsModal(["apps-and-integrations", "salesforce"]);
	}, [openSettingsModal]);

	const dismissAutomationProTip = useCallback(() => {
		store.set(SALESFORCE_AUTOMATION_PRO_TIP_DISMISS_KEY, true);
		setAutomationProTipDismissed(true);
	}, []);

	const setSelectedOptionWho = React.useCallback(
		(values: readonly SalesforceOptionType[] | null) => {
			_setSelectedOptionWho(values?.[0] ?? null);
		},
		[_setSelectedOptionWho],
	);

	const setSelectedOptionWhat = React.useCallback(
		(values: readonly SalesforceOptionType[] | null) => {
			_setSelectedOptionWhat(values?.[0] ?? null);
		},
		[_setSelectedOptionWhat],
	);

	const [dataType, setDataType] = React.useState<SalesforceInstanceType>(lastTypeUsed ?? TASK);

	const onChangeDataType = useCallback(
		(option: SingleValue<SelectorOption>) => {
			if (option) {
				setDataType(option.value as SalesforceInstanceType);
			}
		},
		[setDataType],
	);

	const onChangeIncludeRecap = useCallback(() => {
		setIncludeRecap(!includeRecap);
	}, [includeRecap]);

	const onSendTask = useCallback(() => {
		if (isLoading) return;

		setIsLoading(true);

		const recapTemplateType = selectedRecapTemplate?.value === "GENERAL" ? null : selectedRecapTemplate?.value;

		commitMutation<SalesforceSendNotesAsTaskMutationType>(environment, {
			mutation: sendTaskMutation,
			variables: {
				noteId: noteId,
				salesforceWho: selectedOptionWho?.id,
				salesforceWhat: selectedOptionWhat?.id,
				includeRecap,
				recapTemplateType,
			},
			onCompleted: data => {
				const url = data.sendTaskToSalesforce?.url;
				if (url) {
					const selectedOptionTypes = convertGraphQLTypenamesToSimpleTypes([
						selectedOptionWho?.__typename,
						selectedOptionWhat?.__typename,
					]);

					dispatchAnalyticsTrack("Note sent to Salesforce", {
						createdAs: "Task",
						noteIncluded: !!data.sendTaskToSalesforce.noteVersion,
						recapIncluded: includeRecap,
						autoSent: false,
						relatedTo: selectedOptionTypes,
						sendUxLocation: modalMode ? "Modal" : "Panel",
					});

					toast.success("Task sent to Salesforce successfully", {
						actions: (
							// TODO: Replace with some sort of shared button component that is a react-router link
							<FellowLink className={styles.button} href={url} target="_blank" rel="noopener noreferrer">
								Open Task
							</FellowLink>
						),
						closeOnClick: true,
						closeButton: true,
						draggable: false,
						pauseOnHover: true,
					});
				} else {
					toast.error("We were not able send this task to Salesforce");
				}

				setIsLoading(false);
			},
		});
		onClose();
	}, [
		includeRecap,
		isLoading,
		modalMode,
		noteId,
		onClose,
		selectedOptionWhat?.__typename,
		selectedOptionWhat?.id,
		selectedOptionWho?.__typename,
		selectedOptionWho?.id,
		selectedRecapTemplate?.value,
	]);

	const onSendNote = useCallback(() => {
		if (!selectedOptionParents || selectedOptionParents.length === 0) {
			toast.error("We were not able send this note to Salesforce");
			return;
		}

		if (isLoading) return;

		setIsLoading(true);

		const recapTemplateType = selectedRecapTemplate?.value === "GENERAL" ? null : selectedRecapTemplate?.value;

		commitMutation<SalesforceSendNotesAsNoteMutationType>(environment, {
			mutation: sendNoteMutation,
			variables: {
				noteId,
				salesforceParentIds: selectedOptionParents.map(v => v.id),
				includeRecap,
				recapTemplateType,
			},
			onCompleted: data => {
				const url = data.sendNoteToSalesforce?.url;
				if (url) {
					const typenames = convertGraphQLTypenamesToSimpleTypes(selectedOptionParents.map(p => p.__typename));

					dispatchAnalyticsTrack("Note sent to Salesforce", {
						createdAs: "Note",
						noteIncluded: !!data.sendNoteToSalesforce.noteVersion,
						recapIncluded: includeRecap,
						autoSent: false,
						relatedTo: typenames,
						sendUxLocation: modalMode ? "Modal" : "Panel",
					});

					toast.success("Note sent to Salesforce successfully", {
						actions: (
							<FellowLink className={styles.button} href={url} target="_blank" rel="noopener noreferrer">
								Open Note
							</FellowLink>
						),
						closeOnClick: true,
						closeButton: true,
						draggable: false,
						pauseOnHover: true,
					});
					onClose();
				} else {
					const errorMsg = data.sendNoteToSalesforce?.error;
					toast.error(errorMsg ?? "We were not able send this note to Salesforce");
				}

				setIsLoading(false);
			},
		});
	}, [includeRecap, isLoading, modalMode, noteId, onClose, selectedOptionParents, selectedRecapTemplate?.value]);

	// Here we are just trying to be smart and guess some "What" options, which it's only possible when the value is
	// coming from the panel (i.e. there's a default option)
	const whatOptions =
		selectedOptionWho && defaultOption && selectedOptionWho.id === defaultOption.id
			? whoToWhatOptions(defaultOption)
			: [];

	// - options could have types: Contact, Lead or Account.
	// - whatOptions could have: Account, Opportunity or Case,
	//   but Case is not supported as a parent option, so we exclude these
	//   See: resolve_salesforce_search when searchtype == SalesforceSearchType.PARENT
	const parentOptions = defaultOption
		? [...whoOptions, ...whatOptions.filter(item => item.__typename !== "SalesforceCaseNode")]
		: [];

	const onChangeRecapTemplate = useCallback((option: SingleValue<TemplateOption>) => {
		setSelectedRecapTemplate(option);
	}, []);

	const content = (
		<>
			<Modal.Body className={styles.modalContent}>
				<div>
					<div className={styles.selectorLabel}>Create in Salesforce as</div>
					<Selector
						value={DataTypeOptions.find(o => o.value === dataType)}
						options={DataTypeOptions}
						onChange={onChangeDataType}
					/>
				</div>
				{dataType === TASK && (
					<>
						<div>
							<div className={styles.selectorLabel}>Contact or Lead</div>
							<div className={styles.salesforceSelectContainer}>
								<SalesforceSelect
									setSelectedOption={setSelectedOptionWho}
									options={whoOptions}
									defaultValue={defaultWhoOption ? defaultWhoOption : undefined}
									searchType={SEARCH_TYPE_WHO}
									placeholder="Select a contact or lead…"
								/>
							</div>
						</div>

						<div>
							<div className={styles.selectorLabel}>Object in Salesforce</div>
							<div className={styles.salesforceSelectContainer}>
								<SalesforceSelect
									setSelectedOption={setSelectedOptionWhat}
									options={whatOptions}
									searchType={SEARCH_TYPE_WHAT}
									placeholder="Select an object…"
								/>
							</div>
						</div>
					</>
				)}

				{dataType === NOTE && (
					<div>
						<div className={styles.selectorLabel}>Related to:</div>
						<div className={styles.salesforceSelectContainer}>
							<SalesforceSelect
								options={parentOptions}
								setSelectedOption={setSelectedOptionParents}
								searchType={SEARCH_TYPE_PARENT}
								placeholder="Select an object in Salesforce…"
								isMulti={enhancedNotesEnabled}
							/>
						</div>
					</div>
				)}

				<div>
					<div className={cx(styles.includeRecapSetting, { [styles.disabled]: shouldDisableIncludeRecapCheckbox })}>
						<Checkbox
							id="include-recap-checkbox"
							checked={shouldDisableIncludeRecapCheckbox ? false : includeRecap}
							onCheckedChange={onChangeIncludeRecap}
							disabled={shouldDisableIncludeRecapCheckbox}
						/>
						<label htmlFor="include-recap-checkbox">Include AI recap and link to recording</label>
					</div>
					{shouldShowRecapTemplateOptions && (
						<>
							<label
								className={cx(styles.recapTemplateLabel, {
									[styles.disabled]: shouldDisableIncludeRecapCheckbox || !includeRecap,
								})}
							>
								Recap Template
							</label>
							<div className={styles.salesforceSelectContainer}>
								<Selector<TemplateOption>
									styles={recapSelectorStyles as StylesConfig<TemplateOption, boolean>}
									options={recapTemplateOptions}
									value={selectedRecapTemplate}
									onChange={onChangeRecapTemplate}
									isDisabled={!includeRecap}
								/>
							</div>
						</>
					)}
				</div>
				{sendNoteAutomationData.note?.sendNoteToCrmAutomationIsValid &&
					(sendNotesAutomationIsActive ? (
						<div className={styles.automationInfoBox}>
							<div className={styles.automationActiveInfoBox}>
								<FontAwesomeIcon icon={faInfoCircle} className={styles.infoCircleIcon} />
								<span>
									This note will be automatically sent 3hrs after the meeting. Click &rsquo;Send&rsquo; to also send it
									immediately.
								</span>
							</div>
						</div>
					) : (
						!automationProTipDismissed && (
							<div className={styles.automationInfoBox}>
								Pro tip: You can now automatically send your note and recap to Salesforce after all meetings
								<div className={styles.automationInfoBoxButtons}>
									<Button size="slim" variant="primary" onClick={openIntegrationSettings}>
										Configure
									</Button>
									<Button size="slim" variant="secondary" onClick={dismissAutomationProTip}>
										Dismiss
									</Button>
								</div>
							</div>
						)
					))}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button
					size="normal"
					variant="primary"
					onClick={dataType === TASK ? onSendTask : onSendNote}
					disabled={
						isLoading ||
						(dataType === TASK
							? selectedOptionWhat === null && selectedOptionWho === null
							: (selectedOptionParents ?? []).length === 0)
					}
				>
					Send
				</Button>
			</Modal.Footer>
		</>
	);

	if (!modalMode) {
		return content;
	}

	const dateString = getDateStringForSendNoteModal(new Date(data.note?.eventStart || ""));
	const timeString = data.note?.eventStart ? format(new Date(data.note?.eventStart), "h:mm a") : "";

	return (
		<Modal onClose={props.onClose}>
			<Modal.Header onClose={props.onClose}>
				<Modal.Title>
					Send note to Salesforce
					<div className={styles.modalSubtitle}>
						<strong>{data.note?.title}</strong>
						{dateString && timeString && ` ${dateString} at ${timeString}`}
					</div>
				</Modal.Title>
			</Modal.Header>
			{content}
		</Modal>
	);
};

export default SalesforceSendNotesModal;
